import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { priceCompareAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageTitle from "@/components/PageTitle";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { money } from "@/lib/constants";
import { Search, ExternalLink, Star, TrendingDown, TrendingUp, Bell, BellOff, Trash2, LineChart, Filter, Check, ChevronDown, X, Truck } from "lucide-react";

const isFreeShipping = (val) => /free/i.test(String(val || ""));
import { toast } from "sonner";

function TinySpark({ points }) {
  if (!points || points.length < 2) return null;
  const prices = points.map((p) => p.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = Math.max(max - min, 0.01);
  const w = 120;
  const h = 32;
  const step = w / (points.length - 1);
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(h - ((p.price - min) / range) * h).toFixed(1)}`).join(" ");
  const last = prices[prices.length - 1];
  const first = prices[0];
  const up = last >= first;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-8 w-[120px]" aria-hidden>
      <path d={d} stroke={up ? "hsl(0 84% 60%)" : "hsl(145 65% 50%)"} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

export default function PriceCompare() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [savedMap, setSavedMap] = useState({});
  const [savedList, setSavedList] = useState([]);
  const [historyByResult, setHistoryByResult] = useState({});
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef(null);
  const [displayCurrency, setDisplayCurrency] = useState(user?.preferred_currency === "CAD" ? "CAD" : "USD");
  const [region, setRegion] = useState("ALL");

  useEffect(() => {
    if (!categoryOpen) return;
    const onDocClick = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [categoryOpen]);

  const refreshSaved = useCallback(async () => {
    if (!user) return;
    try {
      const res = await priceCompareAPI.listSaved();
      const list = res.data || [];
      setSavedList(list);
      const m = {};
      list.forEach((s) => { m[`${s.query_key}|${s.retailer_key}|${s.title_key}`] = s.id; });
      setSavedMap(m);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { refreshSaved(); }, [refreshSaved]);

  const norm = (v) => (v || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  const savedIdFor = (r) => savedMap[`${norm(query)}|${norm(r.source)}|${norm(r.title)}`];

  const loadHistoryFor = async (r) => {
    try {
      const res = await priceCompareAPI.getPriceHistory({ query, retailer: r.source, title: r.title, days: 90 });
      setHistoryByResult((prev) => ({ ...prev, [r.id]: res.data?.points || [] }));
    } catch { /* ignore */ }
  };

  const search = async (e, overrides = {}) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    setSelectedCategories(new Set());
    try {
      const res = await priceCompareAPI.search({
        q: query,
        sort_by: "price_low",
        display_currency: overrides.currency || displayCurrency,
        region: overrides.region || region,
      });
      const list = res.data?.results || [];
      setResults(list);
      list.forEach((r) => loadHistoryFor(r));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const changeCurrency = (cur) => {
    setDisplayCurrency(cur);
    if (searched && query.trim()) search(null, { currency: cur });
  };

  const changeRegion = (reg) => {
    setRegion(reg);
    if (searched && query.trim()) search(null, { region: reg });
  };

  const toggleSave = async (r) => {
    if (!user) { toast.error("Sign in to save products"); return; }
    const existingId = savedIdFor(r);
    try {
      if (existingId) {
        await priceCompareAPI.deleteSaved(existingId);
        toast.success("Removed from price watch");
      } else {
        await priceCompareAPI.saveProduct({
          query, retailer: r.source, title: r.title,
          price: r.price, currency: r.currency || "USD",
          image_url: r.thumbnail || "", product_url: r.product_link || r.source_url || "",
        });
        toast.success("Saved — you'll be notified on price changes");
      }
      refreshSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update watch");
    }
  };

  const removeSaved = async (id) => {
    try { await priceCompareAPI.deleteSaved(id); refreshSaved(); toast.success("Removed"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not remove"); }
  };

  const lowest = results.length ? Math.min(...results.map((r) => r.price)) : null;

  const availableCategories = useMemo(() => {
    const set = new Set();
    results.forEach((r) => {
      const c = (r.category || "").trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [results]);

  const filteredResults = useMemo(() => {
    if (!selectedCategories.size) return results;
    return results.filter((r) => r.category && selectedCategories.has(r.category));
  }, [results, selectedCategories]);

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const clearCategories = () => setSelectedCategories(new Set());

  const filteredLowest = filteredResults.length ? Math.min(...filteredResults.map((r) => r.price)) : null;

  return (
    <div className="space-y-6">
      <PageTitle
        title="Price Compare"
        subtitle="Find the best price across sim racing retailers. Save products to get notified on price changes."
      />

      <form onSubmit={search} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. Fanatec CSL DD, Moza R5..." className="pl-9" data-testid="price-search-input" />
        </div>
        <div className="flex overflow-hidden rounded-md border border-border" data-testid="price-region-toggle">
          {[["ALL", "All"], ["US", "US"], ["CA", "CA"]].map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => changeRegion(val)}
              data-testid={`price-region-${val}`}
              className={`px-3 py-2 text-xs font-bold transition-colors ${region === val ? "bg-primary text-primary-foreground" : "bg-secondary/40 text-muted-foreground hover:text-foreground"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex overflow-hidden rounded-md border border-border" data-testid="price-currency-toggle">
          {["USD", "CAD"].map((cur) => (
            <button
              key={cur}
              type="button"
              onClick={() => changeCurrency(cur)}
              data-testid={`price-currency-${cur}`}
              className={`px-3 py-2 text-xs font-bold transition-colors ${displayCurrency === cur ? "bg-primary text-primary-foreground" : "bg-secondary/40 text-muted-foreground hover:text-foreground"}`}
            >
              {cur}
            </button>
          ))}
        </div>
        <Button type="submit" disabled={loading} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="price-search-button">Search</Button>
      </form>

      {loading ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}</div>
      ) : results.length ? (
        <div className="space-y-3" data-testid="price-results">
          {availableCategories.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="price-category-filter">
              <div className="relative" ref={categoryRef}>
                <button
                  type="button"
                  onClick={() => setCategoryOpen((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-1.5 text-xs font-semibold text-foreground hover:border-primary/60"
                  data-testid="price-category-toggle"
                >
                  <Filter className="h-3.5 w-3.5" />
                  Categories
                  {selectedCategories.size > 0 && (
                    <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                      {selectedCategories.size}
                    </span>
                  )}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
                </button>
                {categoryOpen && (
                  <div className="absolute left-0 top-full z-30 mt-1 w-64 rounded-md border border-border bg-card p-2 shadow-lg" data-testid="price-category-menu">
                    <div className="max-h-64 overflow-y-auto">
                      {availableCategories.map((cat) => {
                        const active = selectedCategories.has(cat);
                        return (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => toggleCategory(cat)}
                            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-secondary/60 ${active ? "text-primary" : "text-foreground"}`}
                            data-testid={`price-category-option-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <span className={`flex h-4 w-4 items-center justify-center rounded border ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`}>
                              {active && <Check className="h-3 w-3" />}
                            </span>
                            <span className="flex-1 truncate">{cat}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {Array.from(selectedCategories).map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  className="inline-flex items-center gap-1 rounded-full border border-primary/50 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary hover:bg-primary/20"
                  data-testid={`price-category-chip-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {cat}
                  <X className="h-3 w-3" />
                </button>
              ))}
              {selectedCategories.size > 0 && (
                <button
                  type="button"
                  onClick={clearCategories}
                  className="text-[11px] font-semibold text-muted-foreground hover:text-foreground"
                  data-testid="price-category-clear"
                >
                  Clear
                </button>
              )}
              <span className="ml-auto text-[11px] text-muted-foreground">
                {filteredResults.length} of {results.length} results
              </span>
            </div>
          )}

          {filteredResults.length ? filteredResults.map((r) => {
            const saved = Boolean(savedIdFor(r));
            const hist = historyByResult[r.id] || [];
            const isBest = r.price === (selectedCategories.size ? filteredLowest : lowest);
            return (
              <Card key={r.id} className="flex items-center gap-4 border-border bg-card p-4">
                <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md bg-secondary">
                  {r.thumbnail && <img src={r.thumbnail} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {r.source_logo && <img src={r.source_logo} alt="" className="h-4 w-4 rounded" />}
                    <span className="text-xs text-muted-foreground">{r.source}</span>
                    {r.category && (
                      <span className="rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground" data-testid={`price-result-category-${r.id}`}>
                        {r.category}
                      </span>
                    )}
                    {isBest && <Badge className="border-0 bg-emerald-500/20 text-emerald-400" data-testid={`price-best-badge-${r.id}`}>Best price</Badge>}
                  </div>
                  <p className="truncate font-medium">{r.title}</p>
                  <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{r.stock_status}</span>
                    {r.rating && <span className="flex items-center gap-0.5"><Star className="h-3 w-3 text-accent" /> {r.rating}</span>}
                  </div>
                  <div className="mt-2">
                    {isFreeShipping(r.shipping_cost) ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-400" data-testid={`price-ship-free-${r.id}`}>
                        <Truck className="h-3.5 w-3.5" /> Free Shipping
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/15 px-2.5 py-1 text-[11px] font-semibold text-amber-400" data-testid={`price-ship-checkout-${r.id}`}>
                        <Truck className="h-3.5 w-3.5" /> Shipping calculated at checkout
                      </span>
                    )}
                  </div>
                  {hist.length >= 2 && (
                    <div className="mt-2 flex items-center gap-2">
                      <TinySpark points={hist} />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60">{hist.length}pt trend</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 text-right">
                  <p className="font-display text-lg font-bold text-primary">{money(r.price, r.currency)}</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={saved ? "default" : "outline"}
                      onClick={() => toggleSave(r)}
                      data-testid={`price-save-${r.id}`}
                      className={saved ? "bg-primary text-primary-foreground" : ""}
                    >
                      {saved ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                    </Button>
                    <a
                      href={r.product_link}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => { priceCompareAPI.trackClick({ query, retailer: r.source, title: r.title, product_url: r.product_link || r.source_url || "" }).catch(() => {}); }}
                    >
                      <Button size="sm" variant="outline" data-testid={`price-view-${r.id}`}>
                        View <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    </a>
                  </div>
                </div>
              </Card>
            );
          }) : (
            <p className="py-8 text-center text-sm text-muted-foreground">No products in the selected {selectedCategories.size === 1 ? "category" : "categories"}.</p>
          )}
        </div>
      ) : searched ? (
        <p className="py-16 text-center text-sm text-muted-foreground">No results found. Try a different search.</p>
      ) : (
        <p className="py-16 text-center text-sm text-muted-foreground">Search for a product to compare prices.</p>
      )}

      {user && savedList.length > 0 && (
        <section className="mt-10 rounded-xl border border-border bg-card/60 p-6" data-testid="price-saved-section">
          <div className="mb-4 flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-bold uppercase">Your price watch</h2>
            <span className="ml-auto rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-primary">{savedList.length}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {savedList.map((s) => {
              const up = (s.price_delta || 0) > 0;
              return (
                <div key={s.id} className="flex items-start gap-3 rounded-lg border border-border bg-background/60 p-3">
                  <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded bg-secondary">
                    {s.image_url && <img src={s.image_url} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold" title={s.title}>{s.title}</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground/60">{s.retailer} · saved at {money(s.saved_price, s.currency)}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="font-display text-base font-bold text-primary">{money(s.last_price, s.currency)}</span>
                      {s.price_delta !== 0 && (
                        <span className={`flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-widest ${up ? "text-red-400" : "text-emerald-400"}`}>
                          {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {s.price_delta_pct > 0 ? "+" : ""}{s.price_delta_pct}%
                        </span>
                      )}
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeSaved(s.id)} data-testid={`price-saved-remove-${s.id}`}>
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground/60">
            <LineChart className="h-3 w-3" /> You&apos;ll get notified when a watched product&apos;s price changes.
          </p>
        </section>
      )}
    </div>
  );
}
